import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Activity, TrendingUp, TrendingDown, CheckCircle, ArrowRight } from 'lucide-react';
import { getAssetStatus, getStatusFromBps, getStatusConfig, formatBps } from '../services/emergentApiClient';
import { Link } from 'react-router-dom';

const PegStatusCard = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const data = await getAssetStatus('USDC');
        setStatus(data);
      } catch (error) {
        console.error('Erreur récupération status peg:', error);
        // Fallback data pour ne pas casser l'affichage
        setStatus({
          asset: 'USDC',
          price: 0.9999,
          deviation_bps: 0,
          status: 'STABLE',
          last_update: new Date().toISOString()
        });
      } finally {
        setLoading(false);
      }
    };

    fetchStatus();
  }, []);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-12 relative z-10">
        <Card className="bg-white shadow-xl">
          <CardContent className="p-6">
            <div className="animate-pulse flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-gray-200 rounded-full"></div>
                <div>
                  <div className="h-4 bg-gray-200 rounded w-24 mb-2"></div>
                  <div className="h-3 bg-gray-200 rounded w-16"></div>
                </div>
              </div>
              <div className="h-10 bg-gray-200 rounded w-32"></div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!status) return null;

  const statusFromBps = getStatusFromBps(status.deviation_bps);
  const statusConfig = getStatusConfig(statusFromBps);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-12 relative z-10">
      <Card className="bg-white shadow-xl hover:shadow-2xl transition-shadow duration-300">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            
            {/* Status info */}
            <div className="flex items-center space-x-4">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${statusConfig.bgColor}`}>
                <span className="text-2xl">{statusConfig.icon}</span>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Peg {status.asset}
                </h3>
                <div className="flex items-center space-x-2">
                  <span className={`text-sm font-medium ${statusConfig.color}`}>
                    {formatBps(status.deviation_bps)} • {statusConfig.label}
                  </span>
                  {status.deviation_bps > 0 ? (
                    <TrendingUp className="w-4 h-4 text-red-500" />
                  ) : status.deviation_bps < 0 ? (
                    <TrendingDown className="w-4 h-4 text-blue-500" />
                  ) : (
                    <CheckCircle className="w-4 h-4 text-green-500" />
                  )}
                </div>
              </div>
            </div>

            {/* CTA Button */}
            <Link 
              to="/peg-status"
              className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors duration-200 shadow-sm hover:shadow-md"
            >
              Voir le statut du peg
              <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
            
          </div>
          
          {/* Additional info */}
          <div className="mt-4 pt-4 border-t border-gray-100">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-xs text-gray-500">Prix USD</p>
                <p className="text-sm font-semibold">${status.price.toFixed(4)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Déviation</p>
                <p className={`text-sm font-semibold ${statusConfig.color}`}>
                  {formatBps(status.deviation_bps)}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Status</p>
                <p className={`text-sm font-semibold ${statusConfig.color}`}>
                  {statusConfig.label}
                </p>
              </div>
            </div>
          </div>
          
        </CardContent>
      </Card>
    </div>
  );
};

export default PegStatusCard;